// --------------------------------------------------------------
// Created On: 2025-07-15
// Author: Dimitra Weinstein
//
// Last Modified: 2025-11-02
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import useReduxSelector from "src/hooks/useReduxSelector";
import TabList from "src/components/TabList/TabList";
import SearchAssetList from "src/pages/SearchPage/SearchAssetList/SearchAssetList";
import { RPM_COMPANY } from "src/constants/miscellaneous";
import SearchDeviceList from "../SearchDeviceList/SearchDeviceList";

// Container for the search results list, which can display either assets or monitoring devices based on the selected tab.
export default function SearchListContainer(props: Props): Component {
  const [sortAscending, setSortAscending] = useState<boolean>(false);
  const [sortColumnIndex, setSortColumnIndex] = useState<number>(1);
  const currentUser = useReduxSelector((state) => state.currentUser);

  // Update sorting order.
  function updateSort(newIndex: number): void {
    if (newIndex === sortColumnIndex) {
      setSortAscending(!sortAscending);
    } else {
      setSortAscending(false);
      setSortColumnIndex(newIndex);
    }
  }

  return (
    <div className="my-4 search-list-container">
      <div className="row mx-auto">
        <TabList tabs={props.tabs} selectedTabId={props.selectedTabId} onSelect={(tabId) => props.onClickTab(tabId)} />
      </div>
      {props.selectedTabId === "assets" && (
        <SearchAssetList
          loading={props.loading}
          assets={props.assets}
          sortColumnIndex={sortColumnIndex}
          sortAscending={sortAscending}
          crossCompany={currentUser.companyName === RPM_COMPANY}
          onUpdateSort={(index) => updateSort(index)}
          onSortAscendingChange={(ascending) => setSortAscending(ascending)}
          onSortColumnIndexChange={(index) => setSortColumnIndex(index)}
        />
      )}
      {props.selectedTabId === "monitoring-devices" && (
        <SearchDeviceList
          loading={props.loading}
          devices={props.devices}
          deviceStatuses={props.deviceStatuses}
          sortColumnIndex={sortColumnIndex}
          sortAscending={sortAscending}
          crossCompany={currentUser.companyName === RPM_COMPANY}
          onUpdateSort={(index) => updateSort(index)}
          onSortAscendingChange={(ascending) => setSortAscending(ascending)}
          onSortColumnIndexChange={(index) => setSortColumnIndex(index)}
        />
      )}
    </div>
  );
}

interface Props {
  loading: boolean;
  tabs: Tab[];
  assets: Asset[];
  devices: Device[];
  deviceStatuses: DeviceStatus[];
  selectedTabId: string;
  onClickTab: (tabId: string) => void;
}

interface Tab {
  tabId: string;
  name: string;
  shortName: string;
  breakPoints: string;
}

interface Asset {
  assetId: number;
  name: string;
  nickname: string;
  productType: string;
  productModel: string;
  productIdentifier: string;
  companyId: number;
  companyName: string;
  deviceType: string | null;
  deviceIdentifier: string | null;
}

interface Device {
  deviceId: number;
  deviceType: string;
  identifier: string;
  assetName: string;
  subscriptionType: string;
  activationStatus: string;
  subscriptionStartDate: string;
  subscriptionExpiry: string;
  subscriptionNumber: string;
  statuses: Status[];
}

interface Status {
  deviceStatusId: number;
  value: string;
}

interface DeviceStatus {
  code: string;
  description: string;
  deviceStatusId: number;
  name: string;
  value: string;
}
